import type { ReactNode } from 'react'
import { fmtNumber, useT } from '@/i18n'
import { inventoryMessages } from '@/i18n/messages/inventory'
import type { Drum, DrumStatus } from './types'

export const INPUT =
  'w-full rounded-lg border border-neutral-300 px-2.5 py-2 text-base sm:text-sm dark:border-neutral-700 dark:bg-neutral-950'

const STATUS_TONE: Record<DrumStatus, string> = {
  unregistered: 'bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300',
  in_stock: 'bg-sky-100 text-sky-800 dark:bg-sky-500/15 dark:text-sky-300',
  at_site: 'bg-violet-100 text-violet-800 dark:bg-violet-500/15 dark:text-violet-300',
  with_crew: 'bg-green-100 text-green-800 dark:bg-green-500/15 dark:text-green-300',
  written_off: 'bg-neutral-200 text-neutral-500 line-through dark:bg-neutral-800 dark:text-neutral-500',
}

export function StatusBadge({ status }: { status: DrumStatus }) {
  const t = useT(inventoryMessages)
  const label = {
    unregistered: t('statusUnregistered'), in_stock: t('statusInStock'), at_site: t('statusAtSite'),
    with_crew: t('statusWithCrew'), written_off: t('statusWrittenOff'),
  }[status]
  return <span className={`inline-flex whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_TONE[status]}`}>{label}</span>
}

/** Metres left against the delivered length; an estimate is drawn hatched and labelled. */
export function LeftBar({ drum, wide = false }: { drum: Drum; wide?: boolean }) {
  const t = useT(inventoryMessages)
  if (drum.remainingM == null || !drum.lengthDeliveredM) return <span className="text-xs text-neutral-400">—</span>
  const pct = Math.min((drum.remainingM / drum.lengthDeliveredM) * 100, 100)
  return (
    <div className={`flex items-center gap-2 ${wide ? 'w-full' : 'min-w-[8rem]'}`}>
      <div className="h-2 flex-1 overflow-hidden rounded-full bg-neutral-100 dark:bg-neutral-800">
        <div className="h-full rounded-full" style={{
          width: `${pct}%`, backgroundColor: 'var(--viz-series-1)',
          backgroundImage: drum.isEstimate ? 'repeating-linear-gradient(45deg, transparent 0 3px, rgba(255,255,255,.45) 3px 6px)' : undefined,
        }} />
      </div>
      <span className="tabular whitespace-nowrap text-xs text-neutral-600 dark:text-neutral-300">
        {fmtNumber(drum.remainingM)} m{drum.isEstimate && <span className="text-neutral-400"> ({t('estimate')})</span>}
      </span>
    </div>
  )
}

/** Where a drum is, in one line: crew at a store, a store, or nowhere (written off). */
export function whereText(d: Drum): string {
  if (d.kolonaName) return d.locationName ? `${d.kolonaName} · ${d.locationName}` : d.kolonaName
  return d.locationName ?? '—'
}

export function Dialog({ title, hint, onClose, children, footer }:
  { title: string; hint?: string | undefined; onClose: () => void; children: ReactNode; footer?: ReactNode }) {
  const t = useT(inventoryMessages)
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center sm:p-4" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="flex max-h-[92dvh] w-full max-w-lg flex-col rounded-t-2xl bg-white shadow-xl ring-1 ring-black/5 sm:rounded-2xl dark:bg-neutral-900 dark:ring-white/10"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-2 border-b border-neutral-200 p-4 dark:border-neutral-800">
          <div>
            <h2 className="text-lg font-semibold">{title}</h2>
            {hint && <p className="mt-0.5 text-sm text-neutral-500">{hint}</p>}
          </div>
          <button onClick={onClose} aria-label={t('close')}
            className="grid size-11 shrink-0 place-items-center rounded-xl text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700 dark:hover:bg-neutral-800">✕</button>
        </div>
        <div className="space-y-4 overflow-y-auto p-4">{children}</div>
        {footer && <div className="flex justify-end gap-2 border-t border-neutral-200 p-4 dark:border-neutral-800">{footer}</div>}
      </div>
    </div>
  )
}

export function PrimaryButton(props: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return <button type="button" {...props}
    className={`min-h-11 rounded-xl bg-brand-600 px-4 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50 ${props.className ?? ''}`} />
}

export function GhostButton(props: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return <button type="button" {...props}
    className={`min-h-11 rounded-xl px-4 text-sm font-medium text-neutral-700 ring-1 ring-neutral-300 hover:bg-neutral-50 disabled:opacity-50 dark:text-neutral-200 dark:ring-neutral-700 dark:hover:bg-neutral-800 ${props.className ?? ''}`} />
}

export function Field({ label, hint, children }: { label: string; hint?: string | undefined; children: ReactNode }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-xs font-medium text-neutral-600 dark:text-neutral-300">{label}</span>
      {children}
      {hint && <span className="block text-xs text-neutral-500">{hint}</span>}
    </label>
  )
}
